// src/hooks/useWorkPerformedEntries.js
// wpauthor-5d3f8a26 — WORK PERFORMED ATTRIBUTION, SEAM B (web).
//
// Realtime subscription to one job's Work Performed entries:
//
//     organizations/{orgId}/jobs/{jobId}/workPerformedEntries
//
// Work Performed used to be one free-text field. Anyone who could edit the job
// typed into it and nothing recorded who wrote what, so when two people worked a
// job and one wrote a diagnosis that turned out to be wrong there was no record
// of who wrote it. Each entry is now its own document carrying its author.
//
// RETURNS ENTRIES IN CUSTOMER ORDER — oldest first, the same order the flatten
// uses to build job.workPerformed (sortWorkPerformedEntries). This is the one
// place that ordering is decided for the UI, so what a reviewer reads on screen
// is the order the customer reads on the invoice. Notes sort newest-first;
// these deliberately do not.
//
// REMOVED ENTRIES ARE INCLUDED. Removal hides, it never erases — the rules
// refuse a delete outright. A removed entry leaves the customer's invoice
// immediately (the flatten skips it) but stays here so the internal record of
// who wrote what survives the correction. Callers render them greyed; they must
// NOT filter them out.
//
// ── Defense-in-depth notes ────────────────────────────────────────────────
//   1. Staff-only by construction. The entries block in firestore.rules has NO
//      portal clause, so a customer cannot read this path at all (rules test
//      WPA10). Never reuse this hook in a portal-facing view.
//   2. Sorting is client-side, so no composite index is needed — the same
//      choice useOrgNotes makes, and these collections hold a handful of docs.
//   3. Fully scoped to one org and one job; no cross-org read is possible.
//   4. Bails out cleanly when orgId or jobId is missing, so it can be called
//      unconditionally from a modal that may not have a job yet.
//   5. Unsubscribes on unmount and on input change.

import { useEffect, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { sortWorkPerformedEntries, WP_ENTRIES_COLLECTION } from '../utils/workPerformedEntries';

const useWorkPerformedEntries = ({ orgId, jobId }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!orgId || !jobId) {
      setEntries([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    setLoading(true);
    const col = collection(db, `organizations/${orgId}/jobs/${jobId}/${WP_ENTRIES_COLLECTION}`);
    const unsub = onSnapshot(
      col,
      (snap) => {
        setEntries(sortWorkPerformedEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.warn('useWorkPerformedEntries: subscription failed', err);
        setEntries([]);
        setLoading(false);
        setError(err);
      }
    );
    return unsub;
  }, [orgId, jobId]);

  return { entries, loading, error };
};

export default useWorkPerformedEntries;
